/**
 * Money plan theme (admin-only, Oct 2026) - plan_01
 * The look of the money plan section of the Diary app: a soft lilac page,
 * white cards, pastel touches and a calm, text-only tab bar. It sits on top
 * of Pink: the DEVELOPER switch in Settings stores the flag on this phone,
 * and App.js puts data-plan="1" on <html> while the flag is on AND the
 * theme is Pink. Anything not restyled yet stays Pink.
 */
import { useEffect, useState } from 'react';

export const PLAN_KEY = 'worthly_plan';
export const PLAN_TOGGLE_EVENT = 'worthly:plan-toggle';
export const PLAN_APPLIED_EVENT = 'worthly:plan-applied';

export const isPlanFlagSet = () => {
  try { return window.localStorage.getItem(PLAN_KEY) === '1'; } catch (e) { return false; }
};

export const setPlanFlag = (on) => {
  try { window.localStorage.setItem(PLAN_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
  window.dispatchEvent(new Event(PLAN_TOGGLE_EVENT));
};

export const isPlanOn = () =>
  typeof document !== 'undefined' && document.documentElement.getAttribute('data-plan') === '1';

export const applyPlan = (on) => {
  const el = document.documentElement;
  const was = el.getAttribute('data-plan') === '1';
  if (on) el.setAttribute('data-plan', '1');
  else el.removeAttribute('data-plan');
  if (was !== !!on) window.dispatchEvent(new Event(PLAN_APPLIED_EVENT));
};

// True while Money plan is showing. Re-renders when it's switched on or off.
export const usePlan = () => {
  const [on, setOn] = useState(isPlanOn);
  useEffect(() => {
    const sync = () => setOn(isPlanOn());
    sync();
    window.addEventListener(PLAN_APPLIED_EVENT, sync);
    return () => window.removeEventListener(PLAN_APPLIED_EVENT, sync);
  }, []);
  return on;
};

// Shared colours, so every page uses the same family.
export const PLAN = {
  page: '#F4F1FB',
  card: '#FFFFFF',
  ink: '#26252E',
  soft: '#8B879A',
  line: '#EEEAF6',
  field: '#F7F5FB',
  button: '#1F1E24',
  lilac: { bg: '#ECE6FA', text: '#7A5DD3', bar: '#B9A6F0', line: '#A68EEA' },
  mint: { bg: '#DFF2E7', text: '#2F8756', bar: '#9ED9B6', line: '#5DB88A' },
  blue: { bg: '#DFE9F9', text: '#3C6DB5', bar: '#A9C6F0', line: '#7FA6DE' },
  pink: { bg: '#FAE4ED', text: '#C04B7C', bar: '#F2AFC8', line: '#E58AAE' },
  butter: { bg: '#FCF3D2', text: '#8A7A3C', bar: '#F3DE8E', line: '#E9CD6A' },
};

// plan_04: the four pastel tiles pages share in Money plan, in the same
// { bg, label, value } shape as the Pink tile palettes.
export const PLAN_TILES = [
  { bg: '#FAE4ED', label: '#C04B7C', value: '#C04B7C' },
  { bg: '#DFE9F9', label: '#3C6DB5', value: '#3C6DB5' },
  { bg: '#DFF2E7', label: '#2F8756', value: '#2F8756' },
  { bg: '#ECE6FA', label: '#7A5DD3', value: '#7A5DD3' },
];

// plan_08: month-end review colours in Money plan, same shape as
// getReviewPalette(theme).
export const PLAN_REVIEW = {
  ui: { bgTop: '#F4F1FB', bgBottom: '#F4F1FB', button: '#1F1E24', shadow: '0 8px 20px rgba(31,30,36,0.18)' },
  income: { fill: '#FAE4ED', label: '#C04B7C', deep: '#C04B7C' },
  spent: { fill: '#DFE9F9', label: '#3C6DB5', deep: '#3C6DB5' },
  saved: { fill: '#DFF2E7', label: '#2F8756', deep: '#2F8756' },
  leftover: { fill: '#ECE6FA', label: '#7A5DD3', deep: '#7A5DD3' },
};

// plan_09: fund and account colours in Money plan. Same shape as
// getFundTint (wash, ink, bar, mid) plus a soft gradient square and a ring
// colour for the detail page.
export const PLAN_FUND_TINTS = {
  blue: { wash: '#DFE9F9', ink: '#3C6DB5', bar: '#A9C6F0', mid: '#A9C6F0', grad: 'linear-gradient(135deg, #BCD5F4, #DCE8FA)', ring: '#5E8FD6' },
  lilac: { wash: '#ECE6FA', ink: '#7A5DD3', bar: '#B9A6F0', mid: '#B9A6F0', grad: 'linear-gradient(135deg, #D3C6F5, #ECE6FC)', ring: '#A68EEA' },
  pink: { wash: '#FAE4ED', ink: '#C04B7C', bar: '#F2AFC8', mid: '#F2AFC8', grad: 'linear-gradient(135deg, #F6C5D8, #FCE6EF)', ring: '#E889AF' },
  mint: { wash: '#DFF2E7', ink: '#2F8756', bar: '#9ED9B6', mid: '#9ED9B6', grad: 'linear-gradient(135deg, #BFE6CF, #E3F5EA)', ring: '#6FC394' },
  butter: { wash: '#FCF3D2', ink: '#8A7A3C', bar: '#F3DE8E', mid: '#F3DE8E', grad: 'linear-gradient(135deg, #F7E5A6, #FDF5D9)', ring: '#E9CD6A' },
};
const PLAN_FUND_ORDER = ['blue', 'lilac', 'pink', 'blue', 'mint', 'butter', 'lilac'];
export const planFundTint = (index) =>
  PLAN_FUND_TINTS[PLAN_FUND_ORDER[((index || 0) % PLAN_FUND_ORDER.length + PLAN_FUND_ORDER.length) % PLAN_FUND_ORDER.length]];

// w2_01: "Show my money plan" (admin, Developer). Hides your personal money
// plan pieces so you can take screenshots. Kept on this phone, on by default.
export const MONEY_PLAN_KEY = 'worthly_show_money_plan';
export const MONEY_PLAN_EVENT = 'worthly:money-plan-toggle';
export const isMoneyPlanShown = () => {
  try { return window.localStorage.getItem(MONEY_PLAN_KEY) !== '0'; } catch (e) { return true; }
};
export const setMoneyPlanShown = (on) => {
  try { window.localStorage.setItem(MONEY_PLAN_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
  window.dispatchEvent(new Event(MONEY_PLAN_EVENT));
};
// w2_12: developer switch to hide Looking ahead and the next-year switch, for screenshots
export const FUTURE_EXTRAS_KEY = 'worthly_show_future_extras';
export const FUTURE_EXTRAS_EVENT = 'worthly:future-extras-toggle';
export const isFutureExtrasShown = () => {
  try { return window.localStorage.getItem(FUTURE_EXTRAS_KEY) !== '0'; } catch (e) { return true; }
};
export const setFutureExtrasShown = (on) => {
  try { window.localStorage.setItem(FUTURE_EXTRAS_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
  window.dispatchEvent(new Event(FUTURE_EXTRAS_EVENT));
};
export const useFutureExtrasShown = () => {
  const [on, setOn] = useState(isFutureExtrasShown);
  useEffect(() => {
    const sync = () => setOn(isFutureExtrasShown());
    window.addEventListener(FUTURE_EXTRAS_EVENT, sync);
    return () => window.removeEventListener(FUTURE_EXTRAS_EVENT, sync);
  }, []);
  return on;
};

export const useMoneyPlanShown = () => {
  const [on, setOn] = useState(isMoneyPlanShown);
  useEffect(() => {
    const sync = () => setOn(isMoneyPlanShown());
    window.addEventListener(MONEY_PLAN_EVENT, sync);
    return () => window.removeEventListener(MONEY_PLAN_EVENT, sync);
  }, []);
  return on;
};
